<script setup>
import abilities from "@/composables/abilities";
import {computed, nextTick, onMounted, onUpdated, ref, watch, defineProps} from "vue";
import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import Card from "@/Modules/Financial/components/Card";
import Button from "@/components/Button.vue";
import {useStore} from "vuex";
import BoxModal from "@/Modules/Financial/components/Modals/Box/BoxModal"
import DepositBoxModal from "@/Modules/Financial/components/Modals/Box/DepositModal"
import WithdrawalBoxModal from "@/Modules/Financial/components/Modals/Box/WithdrawalModal"
import TransferBoxModal from "@/Modules/Financial/components/Modals/Box/TransferModal"
import TransactionModal from "@/Modules/Financial/components/Modals/TransactionModal"
import { useRoute } from 'vue-router';
import Select2 from "@/components/Select2.vue";
import {formattedLocalAmount} from "@/mixins/Helpers";

import {useFormatAmount} from "@/composables/formatAmount";
import {useFormatDate} from "@/composables/formatDate";
import Img from "@/components/Img.vue";
import i18n from "@/plugins/i18n";
import {getBoxHistoricFile} from "@/Modules/Financial/store/modules/accounts/actions";

const props = defineProps({
  officeId: {
    type: String,
    required: true
  },
  societyId: {
    type: String,
    required: true
  },
  filterStatBox: {
    type: Object,
    required: true
  },
})

const store = useStore()
const route = useRoute()

const monthsNameBox = ref(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'])
const monthNumbersBox = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])

const currentMonthBox = ref(null)
const transaction = ref(null)
const currentBox = ref(null)
const rangeBox = ref(null)
const historiesBox = ref([])
const countBox = ref(0)
const boxesLoaded = ref(false)
const currencyIsoBox = ref('')
const boxData = ref({})
const monthStatisticsBox = ref({
  currency_iso: null,
  statistics: {}
})

const formBox = ref({
  name: '',
  initial_amount: 0,
  currency_id: '',
  office_id: props.officeId
})

const formEditBox = ref({
  id: '',
  name: '',
  initial_amount: 0,
  office_id: props.officeId
})

const formDepositBox = ref({
  box_id: '',
  amount: 0,
  reason: "",
})

const formWithdrawalBox = ref({
  box_id: '',
  amount: 0,
  reason: "",
})

const formTransferBox = ref({
  box_id: '',
  type: 'box',
  credit_box_id: '',
  amount: 0,
  rate: 1,
  reason: '',
  debit_box_id: ''
})
const xAxisRootBox = ref(null);
const mainSeriesBox = ref([]);

const accounts = computed(() => store.getters['accounts/getAccounts'])
const boxes = computed(() => store.getters['accounts/getBoxes'])
const getBoxesToCredit = computed(() => Object.values(boxes.value || {}).filter(a => a.id != currentBox.value))

const amountFormatterBox = useFormatAmount()
const dateFormatterBox = useFormatDate()

watch(() => props.filterStatBox.start_date, async (value) => {
  if(value){
    rangeBox.value = moment(value).format('YYYY')
    LoadBoxTransactionsData()
    initialiseBoxMonthData()
    initialiseDataBox()
  }
})

watch(() => props.filterStatBox.end_date, async (value) => {
  if(value){
    LoadBoxTransactionsData()
    initialiseBoxMonthData()
    initialiseDataBox()
  }
})

watch(() => props.officeId, async(newValue, oldValue) => {
  if(newValue){
    await loadBoxes()

    let firstBox = Object.values(boxes.value)[0];
    if (firstBox) {
      currentBox.value = firstBox.id;
    }else{

      currentBox.value = null
      historiesBox.value = []

      formEditBox.value = {
        id: '',
        name: '',
        initial_amount: 0,
        office_id: props.officeId
      }

      monthStatisticsBox.value = {
        currency_iso: null,
        statistics: {}
      }

      initChartBox([], '')
    }
  }
})

watch(() => boxes.value, async(newValue, oldValue) => {
  if(newValue){
    updateCurrentBox(currentBox.value)

    if (props.filterStatBox.start_date && props.filterStatBox.end_date) {
      await LoadBoxTransactionsData();
    }

    if (currentMonthBox.value) {
      initialiseBoxMonthData(currentMonthBox.value);
    }
    await initDateRangePickerBox()
  }
})

watch(() => currentBox.value, async (newValue, oldValue) => {
  if (newValue !== oldValue) {
    updateCurrentBox(newValue);
    if (props.filterStatBox.start_date && props.filterStatBox.end_date) {
      await LoadBoxTransactionsData();
    }

    if (currentMonthBox.value) {
      initialiseBoxMonthData(currentMonthBox.value);
    }
    loadBoxes()
    await initDateRangePickerBox()
  }
})

watch(() => currentMonthBox.value, () => {
  if(currentMonthBox.value) {
    initialiseBoxMonthData(currentMonthBox.value)
  }
})

const getCurrentBox = computed(() => store.getters['accounts/getBox'](currentBox.value) || {})

const reversedHistoriesBox = computed(() => [...historiesBox.value].reverse());

const currencyBox = computed(() => {
  return Object.values(getCurrentBox.value).length ? {
    iso: getCurrentBox.value.currency_iso,
    symbol: getCurrentBox.value.currency_symbol,
  }: {
    iso: "",
    symbol: "",
  }
})

function updateCurrentBox(id) {
  let box = Object.values(boxes.value || {}).find(a => a.id == id)
  if(box){
    formEditBox.value.id = box.id
    formEditBox.value.name = box.name
    formEditBox.value.initial_amount = box.amount
    currencyIsoBox.value = box.currency_iso
    formDepositBox.value.box_id = box.id;
    formWithdrawalBox.value.box_id = box.id;
    formTransferBox.value.box_id = box.id;
    formTransferBox.value.debit_box_id = box.id;

    boxData.value = box
  }
}

function openBoxModal(name) {
  $('#box_modal_'+name).modal('toggle')
}

function openDepositBoxModal() {
  $('#deposit_box_modal').modal('toggle')
}

function openWithdrawalBoxModal() {
  $('#withdrawal_box_modal').modal('toggle')
}

function openTransferBoxModal() {
  $('#transfer_box_modal').modal('toggle')
}

async function loadBoxes() {
  if(props.officeId) {
    await store.dispatch('accounts/loadBoxesForOffice', props.officeId)
  }
}

function setCurrentBox(box){
  currentBox.value = box
}

onMounted(async () => {
  await loadBoxes()
  boxesLoaded.value = true
  if(props.societyId){
    await store.dispatch('accounts/loadAccountsForCountry', props.societyId)
  }
  store.dispatch('currencies/loadCurrencies')

  if(boxes.value){
    let firstBox = Object.values(boxes.value)[0];
    if (firstBox) {
      currentBox.value = firstBox.id;
    }
  }

  if(props.filterStatBox.start_date && props.filterStatBox.end_date){
    await LoadBoxTransactionsData();
  }

  await initDateRangePickerBox()
})

async function LoadBoxTransactionsData() {
  if(currentBox.value) {
    await axios.get(`/get/box/${currentBox.value}/histories?startDate=${props.filterStatBox.start_date}&endDate=${props.filterStatBox.end_date}`)
        .then((response) => {
          historiesBox.value = response.data.histories
          countBox.value = response.data.count
        })
        .catch((error) => {

        })
  }
}

async function initialiseBoxMonthData(current) {
  if (current === undefined){
    current = parseInt(moment().format('M'))
  }

  let date = rangeBox.value || (props.filterStatBox.start_date ? moment(props.filterStatBox.start_date).format('YYYY') : null);
  if(date && currentBox.value) {
    let response = await store.dispatch('accounts/boxMonthStatistics', {
      account_id: currentBox.value,
      year: date,
      month: current
    })

    if('currency_iso' in response.data){
      monthStatisticsBox.value = response.data
    }
  }
}

async function reloadData () {
  LoadBoxTransactionsData()
  initialiseBoxMonthData()
  loadBoxes()
  await initDateRangePickerBox()
  if(props.societyId){
    await store.dispatch('accounts/loadAccountsForCountry', props.societyId)
  }
}

async function initDateRangePickerBox(){
  var start = moment().subtract(29, "days");
  var end = moment();

  $("#kt_dashboard_daterangepicker_box").datepicker({
    format: "yyyy",
    startView: "years",
    minViewMode: "years",
    autoclose: true,
    maxDate: moment(new Date())
  }, cbBox);

  currentMonthBox.value = parseInt(moment().format('M'))

  await cbBox(start, end, '');
}

async function cbBox(start, end, label) {
  let title = '';

  let year = end.format('Y');

  rangeBox.value = year

  $('#kt_dashboard_daterangepicker_date_box').html(year);
  $('#kt_dashboard_daterangepicker_title_box').html(title);

  if(currentMonthBox.value){
    initialiseBoxMonthData(currentMonthBox.value)
  }else {
    initialiseBoxMonthData()
  }

  initialiseDataBox(year)
}

async function initialiseDataBox(range){
  let date = range || (props.filterStatBox.start_date ? moment(props.filterStatBox.start_date).format('YYYY') : null);
  if(currentBox.value && date && moment(date, 'YYYY', true).isValid()) {
    let response = await store.dispatch('accounts/boxStatistics', {
      account_id: currentBox.value,
      year: date
    })

    let data = [];

    monthNumbersBox.value.forEach(number => {
      let information = response?.data?.statistics[number]
      if(information){
        data.push({
          "month": information.month,
          "credit": information.deposits_value,
          "debit": information.debits_value,
        })
      }
    })
    initChartBox(data, response?.data?.currency_iso)
  }
}

function initChartBox(dataSeries) {
  if(!xAxisRootBox.value && !mainSeriesBox.value.length) {
    am5.ready(() => {

      var root = am5.Root.new("kt_charts_widget_1_chart_box", {
        panX: "none",
        panY: "none",
        maxZoomLevel: 1
      });

      root.setThemes([
        am5themes_Animated.new(root)
      ]);

      var chart = root.container.children.push(am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        paddingLeft: 0,
        wheelX: "none",
        wheelY: "none",
        layout: root.verticalLayout
      }));

      var legend = chart.children.push(
          am5.Legend.new(root, {
            centerX: am5.p50,
            x: am5.p50
          })
      );

      var data = dataSeries


      var xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        minorGridEnabled: true,
        minGridDistance: 30
      })

      var xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
        categoryField: "month",
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      }));

      xAxisRootBox.value = xAxis

      xRenderer.grid.template.setAll({
        location: 1
      })

      xAxis.data.setAll(data);

      var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 0.1
        })
      }));

      const makeSeries = (name, fieldName, color) => {
        var series = chart.series.push(am5xy.ColumnSeries.new(root, {
          name: name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: fieldName,
          categoryXField: "month",
        }));

        series.columns.template.setAll({
          tooltipText: "{name}, {categoryX}:{valueY}",
          width: am5.percent(90),
          tooltipY: 0,
          strokeOpacity: 0,
        });

        series.data.setAll(data);

        series.appear();

        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationY: 0,
            sprite: am5.Label.new(root, {
              text: "{valueY}",
              fill: root.interfaceColors.get("alternativeText"),
              centerY: 0,
              centerX: am5.p50,
              populateText: true
            })
          });
        });

        legend.data.push(series);

        series.columns.template.setAll({
          fill: am5.color(color), stroke: am5.color(color),
          maxWidth: 10
        });

        mainSeriesBox.value[fieldName] = series
      }

      makeSeries("Crédit", "credit", 0x2DA333);
      makeSeries("Débit", "debit", 0xF64E60);
    });
  }else {
    xAxisRootBox.value.data.setAll(dataSeries);
    mainSeriesBox.value['credit'].data.setAll(dataSeries);
    mainSeriesBox.value['debit'].data.setAll(dataSeries);
  }
}

async function viewTransactionDetailsBox(id){
  if(id) {
    let response = await store.dispatch('accounts/loadDetailTransaction', id)
    transaction.value = response.data.transaction
    if(transaction.value){
      $('#detailsTransaction').modal('toggle')
    }
  }
}

function downloadBoxHistoric() {
  const data = {
    id: currentBox.value,
    startDate: props.filterStatBox.start_date,
    endDate: props.filterStatBox.end_date
  }
  store.dispatch('accounts/getBoxHistoricFile', data)
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', boxData.value.name+'_transactions_'+data.startDate+ '_'+ data.endDate +'.xls');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.toastr['success'](i18n.global.t('notification.download_transactions'))
      })
      .catch((error) => {
      }).finally(() => {})
}

</script>

<template>
  <div class="row">
    <div class="col-md-3">
      <Card :title="$t('financial.accounts.info_box')" containerClass="mb-5 shadow-sm" titleContainerClass="d-flex justify-content-center">
        <template #cardBody>
          <div class="form-group row">
            <div class="col-lg-12 col-md-12 col-sm-12">
              <div class="input-group mb-5">
                <div class="input-group-prepend">
                  <button v-if="$can(abilities.UPDATE_BOX)" @click="openBoxModal('edit')" class="btn btn-icon btn-primary" type="button">
                    <i class="fa fa-edit pr-0"></i>
                  </button>
                </div>
                <select id="boxSelect" v-model="currentBox" class="form-control selectpicker" data-size="1" data-live-search="true">
                  <option :value="box.id" v-for="box in boxes" :key="box.id">{{ box.name }}</option>
                </select>
                <div class="input-group-prepend">
                  <button v-if="$can(abilities.CREATE_BOX)" @click="openBoxModal('create')" class="btn btn-icon btn-primary" type="button">
                    <i class="fa fa-plus pr-0"></i>
                  </button>
                </div>
              </div>

              <div class="alert alert-warning my-4 text-center" role="alert" v-if="boxes.length == 0">
                {{ $t('financial.accounts.box_empty') }}
              </div>

              <div class="d-flex align-items-center justify-content-center mb-5" v-if="boxData.amount">
                <div class="text-center">
                  <span class="font-weight-bold font-size-h6 text-dark-75 text-hover-primary">{{boxData.name}}</span>
                  <div class="text-muted">{{formattedLocalAmount(boxData.amount)}} {{ currencyIsoBox }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>
      <Card v-if="currentBox && $can([abilities.DEPOSIT_BOX,abilities.WITHDRAWAL_BOX,abilities.TRANSFER_BOX])" :title="$t('financial.accounts.action')" containerClass="mb-5 shadow-sm" titleContainerClass="d-flex justify-content-center">
          <template #cardBody>
              <div class="row text-center">
              <a v-if="$can(abilities.DEPOSIT_BOX)" class="btn btn-light-success font-weight-bold py-4 px-6 mb-4 text-center btn-block" @click="openDepositBoxModal">{{ $t('financial.accounts.box_deposit') }}</a>
              <a v-if="$can(abilities.WITHDRAWAL_BOX)" class="btn btn-light-danger font-weight-bold py-4 px-6 mb-4 text-center btn-block" @click="openWithdrawalBoxModal">{{ $t('financial.accounts.box_withdrawal') }}</a>
              <a v-if="$can(abilities.TRANSFER_BOX)" class="btn btn-light-warning font-weight-bold py-4 px-6 mb-4 text-center btn-block" @click="openTransferBoxModal">{{ $t('financial.accounts.box_transfer') }}</a>
            </div>
          </template>
      </Card>
    </div>
    <div class="col-md-9">
      <Card containerClass="card shadow-sm mb-5">
        <div class="card-header card-header-tabs-line card-header-stretch">
          <div class="card-toolbar">
            <ul class="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
              <li v-if="$can(abilities.VIEW_STAT_BOX)" class="nav-item">
                <a class="nav-link active btn-active-light" data-bs-toggle="tab" href="#kt_tab_statistics">
                  <span class="nav-icon">
                    <i class="fa fa-chart-bar fa-2x me-3"></i>
                  </span>
                  <span class="nav-text fw-bold fs-3">{{$t('financial.accounts.stats')}}</span>
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#kt_tab_historic">
                  <span class="nav-icon">
                    <i class="fa fa-history fa-2x me-3"></i>
                  </span>
                  <span class="nav-text fw-bold fs-3">{{$t('financial.accounts.historics')}}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <template #cardBody>
          <div class="tab-content" id="myTabContent">
            <div class="tab-pane fade show active" id="kt_tab_statistics" role="tabpanel">
              <div class="row">
                <div class="col-md-12">
                  <div id="kt_charts_widget_1_chart_box"></div>
                  <div class="row mt-4 border-bottom pb-10">
                    <div class="col-md-12 text-center">
                      <button v-for="(monthName, key) in monthsNameBox" @click="currentMonthBox = key + 1" type="button" :key="key" :class="[currentMonthBox - 1 === key ? 'btn-primary': 'btn-light']" class="btn btn-shadow font-weight-bold me-2">{{ monthName }}</button>
                    </div>
                  </div>
                  <div v-if="Object.values(monthStatisticsBox.statistics).length">
                    <div class="col-md-12 mt-10 border-bottom pb-10">
                      <h1>{{$t('financial.accounts.credit_amount')}}</h1>
                      <div class="row mt-5">
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-success">{{ monthStatisticsBox.statistics.deposits?.deposits_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.deposits')}}
                                  </a>
                                  <div class="text-success fs-3">{{ amountFormatterBox.formatNumber(monthStatisticsBox.statistics.deposits?.deposits_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatisticsBox.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-success">{{ monthStatisticsBox.statistics.deposits?.transfers_addition_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.deposits_transfer')}}
                                  </a>
                                  <div class="text-success fs-3">{{ amountFormatterBox.formatNumber(monthStatisticsBox.statistics.deposits?.transfers_addition_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatisticsBox.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-12 mt-10">
                      <h1>{{$t('financial.accounts.debit_amount')}}</h1>
                      <div class="row mt-5">
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-danger">{{ monthStatisticsBox.statistics.debits?.debits_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                  {{$t('financial.accounts.withdrawals')}}
                                  </a>
                                  <div class="text-danger fs-3">{{ amountFormatterBox.formatNumber(monthStatisticsBox.statistics.debits?.debits_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatisticsBox.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-danger">{{ monthStatisticsBox.statistics.debits?.debits_expense_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.withdrawals_expenses')}}
                                  </a>
                                  <div class="text-danger fs-3">{{ amountFormatterBox.formatNumber(monthStatisticsBox.statistics.debits?.debits_expense_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatisticsBox.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-12 col-md-4 col-lg-4 mb-4">
                          <div class="card p-3 h-100" style="background-color: #0072CE0D;">
                            <div class="d-flex flex-stack">
                              <div class="symbol symbol-40px me-4">
                                <div class="symbol-label fs-2 py-10 px-8 bg-white">
                                  <span class="text-danger">{{ monthStatisticsBox.statistics.debits?.transfers_subtract_number }}</span>
                                </div>
                              </div>
                              <div class="d-flex align-items-center flex-row-fluid flex-wrap">
                                <div class="flex-grow-1 me-2">
                                  <a class="text-black fs-6 fw-bolder">
                                    {{$t('financial.accounts.transfers')}}
                                  </a>
                                  <div class="text-danger fs-3">{{ amountFormatterBox.formatNumber(monthStatisticsBox.statistics.debits?.transfers_subtract_value) }} <span class="text-dark-50 font-weight-bold">{{ monthStatisticsBox.currency_iso }}</span></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="tab-pane fade" id="kt_tab_historic" role="tabpanel">
              <div class="row">
                <div class="form-group col-7">
                  <Button type="new" icon-class="fa fa-download" btn-class="btn btn-sm btn-icon me-2" @click.prevent="downloadBoxHistoric" />
                </div>
              </div>
              <div class="table-responsive tableFixHead">
                <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" style="margin-top: 13px !important; cursor:pointer">
                  <thead>
                    <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                      <th width="30%" class="text-center d-none d-md-table-cell" scope="col">{{$t('financial.accounts.date')}}</th>
                      <th width="30%" class="text-center d-none d-md-table-cell" scope="col">{{$t('financial.accounts.description')}}</th>
                      <th width="30%" class="text-center" scope="col">{{$t('financial.accounts.debit')}}</th>
                      <th width="30%" class="text-center" scope="col">{{$t('financial.accounts.credit')}}</th>
                      <th class="text-center" scope="col">{{$t('financial.accounts.sold')}}</th>
                    </tr>
                  </thead>
                  <tbody id="tbodyId" v-if="historiesBox.length">
                    <tr @click.prevent="viewTransactionDetailsBox(history.id)" v-for="history in reversedHistoriesBox" style="cursor: pointer">
                      <td class="text-center d-none d-md-table-cell">{{ dateFormatterBox.formatSampleMyDateWithoutHour(history.created_at) }}</td>
                      <td class="text-center d-none d-md-table-cell">{{ history.description }}</td>
                      <td class="text-center">
                        <span class="text-danger font-weight-boldest">{{ history.type === 'DEBIT' ? amountFormatterBox.formatNumber(history.amountTotal) + ' ' + currencyBox.iso : '' }}</span>
                      </td>
                      <td class="text-center">
                        <span class="text-success font-weight-boldest">{{ history.type === 'CREDIT' ? amountFormatterBox.formatNumber(history.amountTotal) + ' ' + currencyBox.iso : '' }}</span>
                      </td>
                      <td class="text-center">
                        <span class="font-weight-boldest">{{ amountFormatterBox.formatNumber(history.sold)  + ' ' + currencyBox.iso }}</span>
                      </td>
                    </tr>
                  </tbody>
                  <div v-else>
                    <div class="text-center">
                      <div class="pt-8">
                        <Img filename="empty.png" alt="" class="mw-100 h-100px h-sm-150px" />
                      </div>
                    </div>
                  </div>
                </table>
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <TransactionModal id="detailsTransaction" v-model="transaction"/>

    <BoxModal id="box_modal_create" :form="formBox" :office="props.officeId" @load="loadBoxes" @box="setCurrentBox"/>
    <BoxModal id="box_modal_edit" :form="formEditBox" :office="props.officeId" @load="loadBoxes" :edit="true"/>

    <DepositBoxModal id="deposit_box_modal" @load="reloadData" :form="formDepositBox"/>
    <WithdrawalBoxModal id="withdrawal_box_modal" @load="reloadData" :form="formWithdrawalBox"/>
    <TransferBoxModal id="transfer_box_modal" @load="reloadData" :form="formTransferBox" :deviseExchange="currencyIsoBox" :getBoxesToCredit="getBoxesToCredit" :getAccounts="accounts"/>
  </div>
</template>

<style scoped>

#kt_charts_widget_1_chart_box {
  width: 100%;
  height: 400px;
}

.tableFixHead {
  table-layout: fixed;
  border-collapse: collapse;
}


.tableFixHead thead tr {
  display: block;
}

.tableFixHead th,
.tableFixHead  td {
  padding: 5px 10px;
  width: 200px;
}

tbody::-webkit-scrollbar-track
{
  -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,0.3);
  border-radius: 10px;
  background-color: #F5F5F5;
}

tbody::-webkit-scrollbar
{
  width: 12px;
  background-color: #F5F5F5;
}

tbody::-webkit-scrollbar-thumb
{
  border-radius: 10px;
  -webkit-box-shadow: inset 0 0 6px rgba(0,0,0,.3);
  background-color: #bcbaba;
}
tbody {
  height: calc(100vh - 350px) !important;
}

tbody tr{
  height: auto !important;
}

tbody{
  flex: 1 1 auto;
  display: block;
  overflow-y: auto;
  overflow-x: hidden;
}

</style>
